/* screens/kitchen/editors/MenuEditor.jsx — редактор проекта меню (drawer type='menu').
   S4. Вкладки Обзор / Блюда (N) / Оформление / История.
   Обзор: метаданные + DraftDishesEditor (для нового) + read-only «Экономика».
   Блюда: счётчики, +Раздел / +Из списка ТТК / +Создать блюдо, callout стабов с
          кнопкой «Достроить (AI)» (F40 tree-job), DishesSectioned (DnD desktop + ↑/↓).
   Оформление: шаблон/цвета/шрифты/чекбоксы/шапка-подвал + iframe-предпросмотр (split на xl/lg,
          переключатель на sm/xs). Экспорт PDF/DOCX (заглушки). История: VersionsBlock.
   ХЕНДОФФ → frontend/pages/kitchen/MenuEditor.jsx. */

import React from 'react';
import { useChat } from '../../../app/providers/ChatProvider.jsx';
import { useConfirm } from '../../../app/providers/ConfirmProvider.jsx';
import { useToast } from '../../../app/providers/ToastProvider.jsx';
import { useUnsaved } from '../../../app/providers/UnsavedChangesProvider.jsx';
import { DishesSectioned } from '../../../components/kitchen/menu/DishesSectioned.jsx';
import { DraftDishesEditor } from '../../../components/kitchen/menu/DraftDishesEditor.jsx';
import { ListEditor } from '../../../components/kitchen/menu/ListEditor.jsx';
import { EntityEditor, EntityTitleInput } from '../../../components/patterns/EntityEditor.jsx';
import { EntityPickerModal } from '../../../components/patterns/Overlays.jsx';
import { EmptyState, QueryBoundary } from '../../../components/patterns/States.jsx';
import { KindBadge, StatusBadge } from '../../../components/ui/Badge.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { IconButton } from '../../../components/ui/Buttons.jsx';
import { Checkbox, Field, NativeSelect, NumberInput, Segmented, TextInput, Textarea } from '../../../components/ui/Field.jsx';
import { Icon } from '../../../components/ui/Icon.jsx';
import { VersionsBlock } from '../../../components/versioning/VersionsBlock.jsx';
import { hooks } from '../../../hooks/index.jsx';
import {
  getMenuDetailReal, saveMenuFull, deleteMenuReal, duplicateMenuReal,
  menuDishPicker, menuPrintUrl, treeBuildStart, treeBuildStatus, downloadFile,
} from '../../../lib/api/index.js';
import {
  blankMenuDetailReal, MENU_PRESETS, MENU_FONTS, FONT_FAMILY,
} from '../../../lib/adapters/menus.js';
import { aiClosedDetail, treeStopNote } from '../../../lib/aiAccess.js';
import { fmt } from '../../../lib/format.js';
import { RR } from '../../../lib/router.jsx';

  const { useState, useEffect, useMemo } = React;
  const { useFakeQuery, useDrawerStack, useBreakpoint } = hooks;
  const { useSearchParams } = RR;

  const STATUS_OPTS = [{ value: 'draft', label: 'Черновик' }, { value: 'active', label: 'Активно' }, { value: 'archived', label: 'В архиве' }];

  // ── Палитра оформления печатного меню (P1-04) ──
  // ЕДИНЫЙ словарь цветов: значения зеркалят токены ДС (tokens.css). Предпросмотр —
  // изолированный <iframe srcDoc>, в который CSS-переменные не проникают, поэтому
  // в HTML инлайнятся значения ИЗ ЭТОГО словаря, а не разбросанные литералы.
  const MENU_PALETTE = {
    accent: ['#3776FE', '#1C9F62', '#C77E12', '#E5475C', '#0E8E8E', '#8A4FD0', '#16161D'], // c-indigo-600 + статус-акценты
    bg: ['#FFFFFF', '#F7F8FB', '#F2F3F7', '#16161D'],                                     // c-ink-0 / c-sand-* / c-ink-800
    inkDark: '#16161D', inkLight: '#E9EAF0',  // текст: тёмный (c-ink-800) / светлый на тёмном фоне
    dimDark: '#54545F', dimLight: '#9C9EAB',  // приглушённый текст: на светлом / на тёмном фоне
    fallbackAccent: '#3776FE', fallbackBg: '#FFFFFF',
  };
  const SWATCHES = MENU_PALETTE.accent;
  const BG_SWATCHES = MENU_PALETTE.bg;

  // Типографика/отступы предпросмотра — именованные константы из шкалы ДС (P2-01).
  // Кегли — со шкалы (fs-*), отступы кратны 4px, альфа границы — именованная.
  const PREVIEW_SCALE = {
    pad: '28px 32px',          // 28/32 — кратно 4px
    h1: 30, sub: 13, h2: 18,   // 30 (fs-30) / 13 (fs-13) / 18 (fs-18) — со шкалы
    body: 14, meta: 12, foot: 12,
    gap: 12, secTop: 24, secBottom: 8, dishPad: 5, footTop: 28, h1Bottom: 4, subBottom: 24,
    borderAlpha: '33',         // 20% акцента на границе раздела
  };
  let _did = 1;

  function MenuEditor({ id, onClose }) {
    const isNew = id === 'new' || String(id).startsWith('new-');
    const { push, stack } = useDrawerStack();
    const { isMobile, bp } = useBreakpoint();
    const toast = useToast();
    const confirm = useConfirm();
    const chat = useChat ? useChat() : null;
    const unsaved = useUnsaved ? useUnsaved() : null;
    const clearGuard = () => unsaved && unsaved.register && unsaved.register('menu-' + id, false);

    const query = useFakeQuery('menu-' + id, () => (isNew ? Promise.resolve(null) : getMenuDetailReal(id)), [id]);

    const [params] = useSearchParams();
    // &v скоупится к ВЕРХНЕМУ сегменту ?edit (IA §4.1) — читаем v только если этот редактор верхний.
    const isTop = !!stack.length && stack[stack.length - 1].type === 'menu' && String(stack[stack.length - 1].id) === String(id);
    const wantHistory = !isNew && ((isTop && !!params.get('v')) || params.get('etab') === 'history');
    const [menu, setMenu] = useState(null);
    const [dirty, setDirty] = useState(false);
    const [busy, setBusy] = useState(false);
    const [tab, setTab] = useState(wantHistory ? 'history' : 'overview');
    const [nameErr, setNameErr] = useState(null);
    const [stubs, setStubs] = useState([]);        // DraftDishesEditor (новое меню)
    const [picker, setPicker] = useState(null);    // sectionId-цель для добавления блюд (или null)
    const [previewKey, setPreviewKey] = useState(0);
    const [designSide, setDesignSide] = useState('settings'); // mobile toggle
    // F40 W5: достройка рецептов стабов фоновой tree-job ({total, done} | null).
    const [treeGen, setTreeGen] = useState(null);
    const treeTimerRef = React.useRef(null);
    useEffect(() => () => { if (treeTimerRef.current) clearTimeout(treeTimerRef.current); }, []);

    useEffect(() => {
      if (isNew) setMenu(blankMenuDetailReal());
      else if (query.data) setMenu(JSON.parse(JSON.stringify(query.data)));
    }, [query.data, id]);

    // hooks ДО ранних return (правило хуков): guard на null menu внутри
    const previewHtml = useMemo(() => (menu ? buildPreview(menu) : ''), [previewKey, menu]);
    const currentSnapshot = useMemo(() => {
      if (!menu) return null;
      const secs = menu.sections || []; const dsh = menu.dishes || [];
      const secName = {}; secs.forEach((s) => { secName[s.id] = s.title; });
      return { name: menu.name, average_check: menu.average_check, target_food_cost_pct: menu.target_food_cost_pct,
        sections: secs.map((s) => ({ id: s.id, title: s.title })),
        dishes: dsh.map((d) => ({ title: d.title, section: secName[d.section_id] || '', food_cost_pct: d.food_cost_pct })) };
    }, [menu]);

    if (!isNew && (query.loading || (!menu && !query.error))) {
      return <div className="ttk-editor"><div className="ttk-skel">{[0, 1, 2, 3].map((i) => <div key={i} className="skeleton ttk-skel__block" />)}</div></div>;
    }
    if (!isNew && query.error) return <div className="ttk-editor"><QueryBoundary error={query.error} onRetry={query.reload}>{null}</QueryBoundary></div>;
    if (!menu) return null;

    const patch = (p) => { setMenu((m) => ({ ...m, ...p })); setDirty(true); };
    const patchStyle = (p) => { setMenu((m) => ({ ...m, style: { ...m.style, ...p } })); setDirty(true); setPreviewKey((k) => k + 1); };
    const bumpPreview = () => setPreviewKey((k) => k + 1);

    const dishes = menu.dishes || [];
    const sections = menu.sections || [];

    // F40 W5: «Достроить рецепты (AI)» — фоновая tree-job по корню-меню (живёт
    // на беке, переживает закрытие drawer'а; здесь — старт + локальный поллинг).
    const pollTreeGen = (jobId) => {
      treeBuildStatus(jobId).then((st) => {
        const active = !!(st && (st.active || st.status === 'running' || st.status === 'pending'));
        setTreeGen(active ? { total: st.total || 0, done: st.done || 0 } : null);
        if (active) { treeTimerRef.current = setTimeout(() => pollTreeGen(jobId), 1800); return; }
        window.dispatchEvent(new CustomEvent('kitchen:ttks-changed'));
        query.reload();
        // Остановлено закрытым доступом (пространство без тарифа) — называем
        // причину; после оплаты «Достроить рецепты (AI)» продолжит сборку.
        if (treeStopNote(st)) toast.show(treeStopNote(st), 'warning', 8000);
        else if (st && st.failed) toast.show(`Готово, кроме ${st.failed} — попробуйте «Достроить» ещё раз`, 'warning');
        else if (st && st.status === 'cancelled') toast.show('Генерация остановлена', 'muted');
        else toast.show('Рецепты сгенерированы', 'success');
      }).catch(() => { treeTimerRef.current = setTimeout(() => pollTreeGen(jobId), 3000); });
    };
    const rebuildRecipes = () => {
      setTreeGen({ total: stubCount, done: 0 });
      treeBuildStart('menu', menu.id).then((st) => {
        if (st && st.job_id) pollTreeGen(st.job_id);
        else { setTreeGen(null); toast.show('Нечего достраивать', 'muted'); }
      }).catch((err) => {
        setTreeGen(null);
        // 402 subscription_expired — текст бэкенда («…выберите тариф»), не глухое «не удалось».
        const closed = aiClosedDetail(err);
        if (closed) { toast.show(closed, 'warning', 8000); return; }
        toast.show(err && err.code === 'quota_exceeded'
          ? 'Лимит AI-операций тарифа исчерпан' : 'Не удалось запустить генерацию', 'warning');
      });
    };
    const stubCount = dishes.filter((d) => d.is_stub).length;
    const costed = dishes.filter((d) => d.cost_per_portion_rub != null);
    const totalCost = costed.reduce((s, d) => s + d.cost_per_portion_rub, 0);
    const avgCost = costed.length ? Math.round(totalCost / costed.length) : null;

    // ── разделы ──
    const addSection = (title) => { setMenu((m) => ({ ...m, sections: [...m.sections, { id: 's' + Date.now(), title, order_index: m.sections.length }] })); setDirty(true); };
    const renameSection = (sid, title) => { setMenu((m) => ({ ...m, sections: m.sections.map((s) => (s.id === sid ? { ...s, title } : s)) })); setDirty(true); bumpPreview(); };
    const moveSection = (sid, dir) => setMenu((m) => {
      const arr = [...m.sections]; const i = arr.findIndex((s) => s.id === sid); const j = i + dir;
      if (j < 0 || j >= arr.length) return m; const t = arr[i]; arr[i] = arr[j]; arr[j] = t;
      setDirty(true); bumpPreview(); return { ...m, sections: arr };
    });
    const deleteSection = (sid) => setMenu((m) => {
      const rest = m.sections.filter((s) => s.id !== sid);
      const fallback = rest[0] ? rest[0].id : null;
      const dishesNext = m.dishes.map((d) => (d.section_id === sid ? { ...d, section_id: fallback } : d));
      setDirty(true); bumpPreview(); return { ...m, sections: rest, dishes: dishesNext };
    });

    // ── блюда ──
    const moveDish = (did, dir) => setMenu((m) => {
      const arr = [...m.dishes]; const dish = arr.find((x) => x.id === did); if (!dish) return m;
      const sect = arr.filter((x) => x.section_id === dish.section_id);
      const pos = sect.indexOf(dish); const swap = sect[pos + dir]; if (!swap) return m;
      const i1 = arr.indexOf(dish), i2 = arr.indexOf(swap); arr[i1] = swap; arr[i2] = dish;
      setDirty(true); bumpPreview(); return { ...m, dishes: arr };
    });
    const changeSection = (did, sid) => {
      // Значение <select> приходит строкой: '' → null (без раздела), числовая строка → number
      // (реальный id), нечисловая (temp 's123') — как есть. Нужно для маппинга в reorder.
      const norm = (sid === '' || sid == null) ? null : (Number.isNaN(Number(sid)) ? sid : Number(sid));
      setMenu((m) => ({ ...m, dishes: m.dishes.map((d) => (d.id === did ? { ...d, section_id: norm } : d)) })); setDirty(true); bumpPreview();
    };
    const deleteDish = async (did) => {
      const dish = dishes.find((d) => d.id === did);
      const ok = await confirm({ title: 'Убрать блюдо из меню?', body: '«' + (dish ? dish.title : '') + '» будет удалено из меню (ТТК сохранится).', confirmLabel: 'Убрать', tone: 'danger' });
      if (ok) { setMenu((m) => ({ ...m, dishes: m.dishes.filter((d) => d.id !== did) })); setDirty(true); bumpPreview(); }
    };
    const reorder = (fromId, toId) => setMenu((m) => {
      const arr = m.dishes.filter((d) => d.id !== fromId);
      const from = m.dishes.find((d) => d.id === fromId); const to = m.dishes.find((d) => d.id === toId); if (!from || !to) return m;
      const moved = { ...from, section_id: to.section_id };
      const idx = arr.findIndex((d) => d.id === toId);
      arr.splice(idx, 0, moved); setDirty(true); bumpPreview(); return { ...m, dishes: arr };
    });
    const dropToSection = (fromId, sid) => setMenu((m) => {
      const arr = m.dishes.filter((d) => d.id !== fromId);
      const from = m.dishes.find((d) => d.id === fromId); if (!from) return m;
      arr.push({ ...from, section_id: sid }); setDirty(true); bumpPreview(); return { ...m, dishes: arr };
    });
    const openDish = (ttkId) => push('ttk', ttkId);
    // «+ Создать блюдо» — открыть редактор новой ТТК (kind=dish) с намерением привязки к меню.
    // Привязку несём подсказкой в sessionStorage (TTKEditor игнорирует незнакомые ключи) —
    // визуально это открывает drawer создания блюда поверх меню.
    const createDish = () => {
      try { sessionStorage.setItem('kitchen:new-ttk', JSON.stringify({ kind: 'dish', for_menu: { menu_id: id, menu_name: menu.name } })); } catch (e) {}
      push('ttk', 'new');
    };

    const addFromTtk = (picked) => {
      const sid = (typeof picker === 'string' && picker) ? picker : (sections[0] ? sections[0].id : (() => { const s = { id: 's' + Date.now(), title: 'Блюда', order_index: 0 }; setMenu((m) => ({ ...m, sections: [s] })); return s.id; })());
      const newDishes = picked.map((p) => ({
        id: 'd-new-' + (_did++), section_id: sid, ttk_id: p.id, title: p.label,
        cost_per_portion_rub: p.cost || null, selling_price_rub: p.sell || null, output_g: p.output_g || null,
        food_cost_pct: p.fc || null, is_stub: p.stub || false,
      }));
      setMenu((m) => ({ ...m, dishes: [...m.dishes, ...newDishes] })); setDirty(true); bumpPreview(); setPicker(null);
      toast.show('Добавлено блюд: ' + newDishes.length, 'success');
    };
    // Источник цены продажи — сама ТТК (selling_price_rub), меню только отображает (P2-29).
    // Пикер — реальный список ТТК kind=dish; уже добавленные в меню исключаем по ttk_id.
    const dishFetcher = async (q) => {
      const existing = new Set((menu.dishes || []).map((d) => d.ttk_id).filter((x) => x != null));
      const rows = await menuDishPicker(q);
      return rows.filter((r) => !existing.has(r.id));
    };

    const resetStyle = () => { const b = blankMenuDetailReal().style; patchStyle({ ...b, header_text: menu.name }); toast.show('Оформление сброшено', 'muted'); };
    // Экспорт/печать — серверный /print/?fmt= по СОХРАНЁННОЙ модели.
    // Скачивание blob'ом с X-Workspace-Id: window.open не шлёт заголовок,
    // и меню business-пространства ловило 404 «битым» файлом.
    const exportMenu = async (format) => {
      if (isNew) { toast.show('Сначала сохраните меню', 'muted'); return; }
      if (dirty) toast.show('Экспортируется последняя сохранённая версия меню', 'muted');
      const r = await downloadFile(menuPrintUrl(id, format));
      if (!r.ok) toast.show((r.data && r.data.detail) || 'Не удалось экспортировать меню', 'danger');
    };

    const save = async () => {
      if (!menu.name.trim()) { setNameErr('Укажите название'); setTab('overview'); return; }
      setBusy(true); setNameErr(null);
      try {
        await saveMenuFull(isNew ? null : id, isNew ? null : query.data, menu, isNew ? stubs : null);
        window.dispatchEvent(new CustomEvent('kitchen:menus-changed'));
        toast.show(isNew ? 'Проект меню создан' : 'Сохранено. Версия зафиксирована.', 'success');
        setDirty(false); clearGuard(); onClose && onClose();
      } catch (e) {
        toast.show(e && e.detail ? e.detail : 'Не удалось сохранить', 'danger');
        setBusy(false);
      }
    };
    const duplicate = async () => {
      setBusy(true);
      try { const r = await duplicateMenuReal(id); window.dispatchEvent(new CustomEvent('kitchen:menus-changed')); toast.show('Меню дублировано', 'success'); setDirty(false); clearGuard(); push('menu', r.id); }
      catch (e) { toast.show('Не удалось дублировать', 'danger'); setBusy(false); }
    };
    const del = async () => {
      // F41: чекбокс каскада (по умолчанию ВЫКЛ) — удалить и блюда, которые
      // входили только в это меню. Блюда из других меню и занятые как п/ф в
      // чужих ТТК бэк пропустит сам (вернёт их в skipped[]).
      const ok = await confirm({
        title: 'Удалить проект меню?',
        body: 'Меню «' + menu.name + '» будет удалено. Это действие необратимо.',
        confirmLabel: 'Удалить', tone: 'danger',
        checkboxLabel: 'Также удалить блюда, которые входили только в это меню (вместе с историей их версий)',
        checkboxDefault: false,
      });
      if (!ok) return; setBusy(true);
      const cascade = !!(ok && ok.checked);
      try {
        const r = await deleteMenuReal(id, { cascade });
        window.dispatchEvent(new CustomEvent('kitchen:menus-changed'));
        if (cascade) {
          window.dispatchEvent(new CustomEvent('kitchen:ttks-changed'));
          const n = (r && r.deleted_dishes) || 0;
          const skipped = (r && r.skipped) || [];
          let msg = 'Меню удалено' + (n ? `, блюд удалено: ${n}` : '');
          if (skipped.length) msg += `; пропущено ${skipped.length} (в других меню или заняты как п/ф)`;
          toast.show(msg, 'success');
        } else {
          toast.show('Меню удалено', 'success');
        }
        setDirty(false); clearGuard(); onClose && onClose();
      }
      catch (e) { toast.show('Не удалось удалить', 'danger'); setBusy(false); }
    };

    const tabs = [
      { value: 'overview', label: 'Обзор' },
      { value: 'dishes', label: 'Блюда', badge: dishes.length || undefined },
      { value: 'design', label: 'Оформление' },
      { value: 'history', label: 'История', disabled: isNew },
    ].filter((t) => !t.disabled);

    const presetOpts = MENU_PRESETS;
    const fontOpts = MENU_FONTS;
    const style = menu.style || {};

    const footer = (
      <>
        {!isNew && <Button variant="ghost" iconLeft="trash" onClick={del} disabled={busy} className="ttk-foot__del">Удалить</Button>}
        {!isNew && <Button variant="ghost" iconLeft="copy" onClick={duplicate} disabled={busy} className="menu-foot__dup">Дублировать</Button>}
        <div className="grow" />
        <Button variant="ghost" onClick={() => onClose && onClose()} disabled={busy}>Отмена</Button>
        <Button variant="primary" onClick={save} loading={busy} disabled={!dirty && !isNew}>{isNew ? 'Создать' : 'Сохранить'}</Button>
      </>
    );

    const splitDesign = bp === 'xl' || bp === 'lg' || bp === 'md';

    return (
      <EntityEditor
        dirtyKey={'menu-' + id} dirty={dirty}
        titleField={(
          <Field error={nameErr} className="ttk-title-field">
            <EntityTitleInput value={menu.name} placeholder="Название проекта меню" invalid={nameErr} ariaLabel="Название меню"
              onChange={(e) => { patch({ name: e.target.value }); if (nameErr) setNameErr(null); }} />
          </Field>
        )}
        kindBadge={<KindBadge kind="menu" size="sm" />}
        status={!isNew && <StatusBadge status={menu.status} size="sm" />}
        headActions={!isNew && (
          <IconButton icon="sparkles" variant="ghost" size="sm" tooltip="Редактировать в чате"
            onClick={() => {
              if (!chat || !chat.spawnEditChat) { toast.show('Чат недоступен', 'muted'); return; }
              chat.spawnEditChat({ kind: 'menu', id, title: menu.name });
            }} />
        )}
        tabs={tabs} activeTab={tab} onTab={setTab}
        footer={footer}>

        {/* ── Обзор ── */}
        {tab === 'overview' && (
          <div className="menu-overview">
            <Field label="Концепция">
              <Textarea value={menu.concept || ''} minRows={2} placeholder="Кухня, сезон, акценты меню…" onChange={(e) => patch({ concept: e.target.value })} />
            </Field>
            <div className="menu-overview__row">
              <Field label="Средний чек"><NumberInput value={menu.average_check ?? ''} unit="₽" step={50} min={0} onChange={(v) => patch({ average_check: v === '' ? null : Number(v) })} /></Field>
              <Field label="Целевой food cost"><NumberInput value={menu.target_food_cost_pct ?? ''} unit="%" step={1} min={0} onChange={(v) => patch({ target_food_cost_pct: v === '' ? null : Number(v) })} /></Field>
              <Field label="Статус"><NativeSelect value={menu.status} options={STATUS_OPTS} onChange={(e) => patch({ status: e.target.value })} /></Field>
            </div>
            {/* F41: расширенный бриф — пожелания и продуктовая матрица уходят
                в генерацию вместе с концепцией (пункт №7 фидбэка беты). */}
            <Field label="Комментарии и пожелания" hint="Стиль меню, ограничения, что обязательно или нельзя — учитывается при генерации">
              <Textarea value={menu.comments || ''} minRows={2} placeholder="Например: подача современная, без свинины, обязательно 2-3 веган-позиции…" onChange={(e) => patch({ comments: e.target.value })} />
            </Field>
            <Field label="Продуктовая матрица" hint="Ключевые продукты и группы закупа, на которых строится меню">
              <Textarea value={menu.product_matrix || ''} minRows={2} placeholder="Например: креветка 16/20, лосось, страчателла, романо, сезонные томаты…" onChange={(e) => patch({ product_matrix: e.target.value })} />
            </Field>
            {isNew && (
              <section className="menu-overview__draft">
                <h4 className="ttk-sub-title t-caption">Черновик блюд</h4>
                <DraftDishesEditor value={stubs} onChange={setStubs} />
              </section>
            )}
            <section className="menu-econ derived-zone">
              <h4 className="ttk-sub-title t-caption">Экономика <span className="derived-tag">расчёт</span></h4>
              <div className="menu-econ__grid">
                <div className="menu-econ__cell"><span className="t-caption text-dim">Блюд в меню</span><span className="num">{dishes.length}</span></div>
                <div className="menu-econ__cell"><span className="t-caption text-dim">Итоговая себестоимость</span><span className="num">{fmt.money(totalCost)}</span></div>
                <div className="menu-econ__cell"><span className="t-caption text-dim">Средняя себест. порции</span><span className="num">{fmt.money(avgCost)}</span></div>
              </div>
            </section>
          </div>
        )}

        {/* ── Блюда ── */}
        {tab === 'dishes' && (
          <div className="menu-dishes">
            <div className="menu-dishes__counters">
              <span className="menu-counter"><span className="num">{dishes.length}</span> всего</span>
              <span className="menu-counter"><span className="num">{stubCount}</span> черновиков</span>
            </div>

            {stubCount > 0 && (
              <div className="callout callout--warn menu-bulk">
                <div className="menu-bulk__top">
                  <Icon name="alert-triangle" size={18} />
                  <span className="grow">
                    {treeGen
                      ? <>Собираю рецепты и составы: <span className="num">{treeGen.done}/{treeGen.total}</span>…</>
                      : <>{stubCount} {fmt.plural(stubCount, 'блюдо без рецепта', 'блюда без рецепта', 'блюд без рецепта')}. Рецепты генерируются автоматически при утверждении меню в чате — недостающие можно достроить здесь.</>}
                  </span>
                  <Button variant="primary" size="sm" iconLeft="sparkles" loading={!!treeGen} disabled={isNew || dirty} onClick={rebuildRecipes}>Достроить (AI)</Button>
                </div>
              </div>
            )}

            <div className="menu-dishes__actions">
              <ListEditor onAdd={addSection} label="Раздел" />
              <Button variant="secondary" size="sm" iconLeft="plus" onClick={() => setPicker(true)}>Из списка ТТК</Button>
              <Button variant="secondary" size="sm" iconLeft="plus" onClick={createDish}>Создать блюдо</Button>
            </div>

            {sections.length === 0 && dishes.length === 0 ? (
              <EmptyState serif={false} icon="layers" title="В меню пока нет разделов"
                body="Создайте раздел и добавьте блюда из списка ТТК или соберите новое блюдо."
                action={(
                  <div className="menu-empty__actions">
                    <ListEditor onAdd={addSection} label="Создать раздел" />
                    <Button variant="secondary" size="sm" iconLeft="plus" onClick={() => setPicker(true)}>Из списка ТТК</Button>
                  </div>
                )} />
            ) : (
              <DishesSectioned sections={sections} dishes={dishes} mobile={isMobile}
                targetFc={menu.target_food_cost_pct}
                onSectionRename={renameSection} onSectionMove={moveSection} onSectionDelete={deleteSection}
                onDishMove={moveDish} onChangeSection={changeSection}
                onDeleteDish={deleteDish} onOpen={openDish}
                onReorder={reorder} onDropToSection={dropToSection} onAddDish={(sid) => setPicker(sid)} />
            )}
          </div>
        )}

        {/* ── Оформление ── */}
        {tab === 'design' && (
          <div className={'menu-design' + (splitDesign ? ' menu-design--split' : '')}>
            {!splitDesign && (
              <Segmented as="tab" className="menu-design__toggle"
                options={[{ value: 'settings', label: 'Настройки' }, { value: 'preview', label: 'Предпросмотр' }]}
                value={designSide} onChange={setDesignSide} ariaLabel="Раздел оформления" />
            )}

            {(splitDesign || designSide === 'settings') && (
              <div className="menu-design__settings">
                <Field label="Шаблон">
                  <NativeSelect value={style.preset} options={presetOpts} onChange={(e) => patchStyle({ preset: e.target.value })} />
                </Field>
                <ColorField label="Акцентный цвет" value={style.accent_color} swatches={SWATCHES} onChange={(c) => patchStyle({ accent_color: c })} />
                <ColorField label="Цвет фона" value={style.bg_color} swatches={BG_SWATCHES} onChange={(c) => patchStyle({ bg_color: c })} />
                <div className="menu-design__row">
                  <Field label="Шрифт заголовков">
                    <NativeSelect value={style.heading_font} options={fontOpts} onChange={(e) => patchStyle({ heading_font: e.target.value })} />
                  </Field>
                  <Field label="Шрифт текста">
                    <NativeSelect value={style.body_font} options={fontOpts} onChange={(e) => patchStyle({ body_font: e.target.value })} />
                  </Field>
                </div>
                <Field label="Показывать в меню">
                  <div className="menu-design__checks">
                    <Checkbox label="Цены" checked={!!style.show_prices} onChange={(e) => patchStyle({ show_prices: e.target.checked })} />
                    <Checkbox label="Выход (граммы)" checked={!!style.show_weight} onChange={(e) => patchStyle({ show_weight: e.target.checked })} />
                    <Checkbox label="КБЖУ" checked={!!style.show_kbju} onChange={(e) => patchStyle({ show_kbju: e.target.checked })} />
                    <Checkbox label="Аллергены" checked={!!style.show_allergens} onChange={(e) => patchStyle({ show_allergens: e.target.checked })} />
                  </div>
                </Field>
                <Field label="Шапка (заголовок меню)">
                  <TextInput value={style.header_text || ''} placeholder={menu.name || 'Название меню'} onChange={(e) => patchStyle({ header_text: e.target.value })} />
                </Field>
                <Field label="Подвал">
                  <TextInput value={style.footer_text || ''} placeholder="Напр. «Приятного аппетита!»" onChange={(e) => patchStyle({ footer_text: e.target.value })} />
                </Field>
                <div className="menu-design__export">
                  <Button variant="secondary" size="sm" iconLeft="download" onClick={() => exportMenu('pdf')}>Экспорт PDF</Button>
                  <Button variant="secondary" size="sm" iconLeft="download" onClick={() => exportMenu('docx')}>Экспорт DOCX</Button>
                  <div className="grow" />
                  <Button variant="ghost" size="sm" iconLeft="refresh" onClick={resetStyle}>Сбросить</Button>
                </div>
              </div>
            )}

            {(splitDesign || designSide === 'preview') && (
              <div className="menu-design__preview">
                <span className="menu-design__preview-bar t-caption text-dim"><Icon name="eye" size={14} />Предпросмотр</span>
                <iframe className="menu-preview-frame" title="Предпросмотр меню" srcDoc={previewHtml} sandbox="" />
              </div>
            )}
          </div>
        )}

        {/* ── История ── */}
        {tab === 'history' && <VersionsBlock kind="menu" id={id} currentSnapshot={currentSnapshot} />}

        {picker && (
          <EntityPickerModal open multi size="md" title="Добавить блюда из ТТК"
            placeholder="Поиск по блюдам…" fetcher={dishFetcher} submitLabel="Добавить"
            onClose={() => setPicker(null)} onSubmit={addFromTtk} />
        )}
      </EntityEditor>
    );
  }

  /* ── ColorField — свотчи + нативный пикер ── */
  function ColorField({ label, value, swatches, onChange }) {
    return (
      <Field label={label}>
        <div className="color-field">
          <div className="color-field__swatches">
            {swatches.map((c) => (
              <button type="button" key={c} className={'color-swatch' + (c.toLowerCase() === (value || '').toLowerCase() ? ' is-active' : '')}
                style={{ '--sw': c }} onClick={() => onChange(c)} aria-label={c} title={c} />
            ))}
          </div>
          <label className="color-field__custom">
            <input type="color" value={value || '#000000'} onChange={(e) => onChange(e.target.value)} />
            <span className="num">{value}</span>
          </label>
        </div>
      </Field>
    );
  }

  /* ── HTML-предпросмотр меню (изолированный srcDoc) ──
     Цвета — из MENU_PALETTE (P1-04), типографика/отступы — из PREVIEW_SCALE (P2-01),
     числа — через fmt.money/fmt.grams (неразр. пробел + группировка + кг-rollover, P3-17). */
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c])); }
  function buildPreview(menu) {
    const st = menu.style || {};
    const P = MENU_PALETTE; const S = PREVIEW_SCALE;
    const accent = st.accent_color || P.fallbackAccent;
    const bg = st.bg_color || P.fallbackBg;
    const dark = /^#(0|1|2)/.test(bg);
    const text = dark ? P.inkLight : P.inkDark;
    const dim = dark ? P.dimLight : P.dimDark;
    // шрифты — slug (serif|sans|condensed) → реальная гарнитура из набора ДС (FONT_FAMILY)
    const hf = FONT_FAMILY[st.heading_font] || FONT_FAMILY.serif;
    const bf = FONT_FAMILY[st.body_font] || FONT_FAMILY.sans;
    const secs = (menu.sections || []).map((s) => {
      const items = (menu.dishes || []).filter((d) => d.section_id === s.id);
      if (!items.length) return '';
      const rows = items.map((d) => {
        const meta = [];
        if (st.show_weight && d.output_g != null) meta.push(esc(fmt.grams(d.output_g)));
        const price = st.show_prices && d.selling_price_rub != null ? '<span class="p">' + esc(fmt.money(d.selling_price_rub)) + '</span>' : '';
        return '<div class="dish"><span class="dn">' + esc(d.title) + (meta.length ? ' <span class="dm">· ' + meta.join(' · ') + '</span>' : '') + '</span>' + price + '</div>';
      }).join('');
      return '<section><h2>' + esc(s.title) + '</h2>' + rows + '</section>';
    }).join('');
    return '<!doctype html><html><head><meta charset="utf-8"><style>'
      + 'body{margin:0;padding:' + S.pad + ';background:' + bg + ';color:' + text + ';font-family:' + bf + ',Geist,sans-serif;}'
      + 'h1{font-family:' + hf + ',serif;color:' + accent + ';font-size:' + S.h1 + 'px;margin:0 0 ' + S.h1Bottom + 'px;}'
      + '.sub{color:' + dim + ';font-size:' + S.sub + 'px;margin:0 0 ' + S.subBottom + 'px;}'
      + 'h2{font-family:' + hf + ',serif;color:' + accent + ';font-size:' + S.h2 + 'px;margin:' + S.secTop + 'px 0 ' + S.secBottom + 'px;border-bottom:1px solid ' + accent + S.borderAlpha + ';padding-bottom:' + S.h1Bottom + 'px;}'
      + '.dish{display:flex;justify-content:space-between;gap:' + S.gap + 'px;padding:' + S.dishPad + 'px 0;font-size:' + S.body + 'px;}'
      + '.dm{color:' + dim + ';font-size:' + S.meta + 'px;}.p{font-variant-numeric:tabular-nums;white-space:nowrap;}'
      + 'footer{margin-top:' + S.footTop + 'px;color:' + dim + ';font-size:' + S.foot + 'px;text-align:center;}'
      + '</style></head><body>'
      + '<h1>' + esc(st.header_text || menu.name || 'Меню') + '</h1>'
      + (menu.concept ? '<p class="sub">' + esc(menu.concept) + '</p>' : '')
      + (secs || '<p class="sub">Добавьте блюда, чтобы увидеть предпросмотр.</p>')
      + (st.footer_text ? '<footer>' + esc(st.footer_text) + '</footer>' : '')
      + '</body></html>';
  }

export { MenuEditor };
